import React from 'react'
import classNames from 'classnames/bind'
import { Link } from '@inertiajs/react'
import Badge from '@/admin/shared/components/Badge/Badge'
import Tabs from '@/admin/shared/components/Tabs/Tabs'
import type { PlanLimitsInfo, PlanUsageInfo, UserProfile, PlanInfo, ActivityHeatmapEntry } from '../types'
import styles from '../UserProfile.module.scss'
import OverviewTab from './tabs/OverviewTab'
import TimelineTab from './tabs/TimelineTab'
import SettingsTab from './tabs/SettingsTab'
import PlanLimitsTab from './tabs/PlanLimitsTab'
import CancelRemovalButton from './CancelRemovalButton'

const cx = classNames.bind(styles)

function initials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('')
}


interface UserProfileCardProps {
  user: UserProfile
  plan: PlanInfo
  limits: PlanLimitsInfo
  usage: PlanUsageInfo
  activityHeatmap: ActivityHeatmapEntry[]
}

export default function UserProfileCard({ user, plan, limits, usage, activityHeatmap }: UserProfileCardProps) {

  const joinedDate = new Date(user.created_at).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  const lastActivityDate = user.last_activity_at
    ? new Date(user.last_activity_at).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null

  const isVerified = user.email_verified_at !== null
  const isActive = user.status === 'active'

  const removalDate = user.remove_date
    ? (() => {
        const d = new Date(user.remove_date)
        d.setHours(d.getHours() + 24)
        return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      })()
    : null

  return (
    <>
      <Link href="/admin/users" className={cx('backLink')}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
        Back to Users
      </Link>

      <div className={cx('grid')}>
        {/* ── Sidebar ── */}
        <aside>
          {/* Identity card */}
          <div className={cx('card')}>
            <div className={cx('cardBody')}>
              <div className={cx('avatarWrap')}>
                <div className={cx('avatar')} aria-hidden="true">
                  {initials(user.name)}
                </div>
                <h2 className={cx('userName')}>{user.name}</h2>
                <p className={cx('userEmail')}>{user.email}</p>
                <Badge variant={isActive ? 'success' : 'danger'} dot>
                  {isActive ? 'Active' : 'Disabled'}
                </Badge>
                <span className={cx('planBadge')}>{plan.title} Plan</span>
                {removalDate && (
                  <Badge variant="danger" dot className={cx('badgeMt')}>
                    Scheduled for removal
                  </Badge>
                )}
              </div>

              {/* Stats */}
              <ul className={cx('statsList')}>
                <li className={cx('statsItem')}>
                  <span className={cx('statsLabel')}>User ID</span>
                  <span className={cx('statsValue')}>#{user.id}</span>
                </li>
                <li className={cx('statsItem')}>
                  <span className={cx('statsLabel')}>Notes</span>
                  <span className={cx('statsValue')}>{user.notes_count}</span>
                </li>
                <li className={cx('statsItem')}>
                  <span className={cx('statsLabel')}>Role</span>
                  <span className={cx('statsValue')}>{user.is_admin ? 'Admin' : 'User'}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* About card — kept for future extension (location, bio, skills, etc.) */}
          <div className={cx('card')} style={{ marginTop: 16 }}>
            <div className={cx('cardHeader')}>About</div>
            <div className={cx('cardBody')}>
              <div className={cx('aboutItem')}>
                <svg className={cx('aboutIcon')} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
                <div>
                  <div className={cx('aboutLabel')}>Joined</div>
                  <div className={cx('aboutValue')}>{joinedDate}</div>
                </div>
              </div>
              <div className={cx('aboutItem')}>
                <svg className={cx('aboutIcon')} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <div>
                  <div className={cx('aboutLabel')}>Account type</div>
                  <div className={cx('aboutValue')}>{user.is_admin ? 'Administrator' : 'Standard user'}</div>
                </div>
              </div>
              <div className={cx('aboutItem')}>
                <svg className={cx('aboutIcon')} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z" />
                  <path d="M12 6v6l4 2" />
                </svg>
                <div>
                  <div className={cx('aboutLabel')}>Last activity</div>
                  <div className={cx('aboutValue')}>{lastActivityDate ?? 'Never'}</div>
                </div>
              </div>
              {/* Placeholder: location, bio, skills can be added here */}
              {removalDate && (
                <div className={cx('aboutItem')}>
                  <svg className={cx('aboutIcon')} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14H6L5 6" />
                    <path d="M10 11v6M14 11v6" />
                    <path d="M9 6V4h6v2" />
                  </svg>
                  <div>
                    <div className={cx('aboutLabel')}>Scheduled removal</div>
                    <div className={cx('aboutValue')} style={{ color: 'var(--danger)' }}>{removalDate}</div>
                  </div>
                </div>
              )}
              {removalDate && <CancelRemovalButton userId={user.id} />}
            </div>
          </div>
        </aside>

        {/* ── Main content ── */}
        <div className={cx('card')}>
          <Tabs
            defaultTab="overview"
            tabs={[
              { id: 'overview', label: 'Overview', content: <OverviewTab user={user} joinedDate={joinedDate} activityHeatmap={activityHeatmap} /> },
              { id: 'plan-limits', label: 'Plan & Limits', content: <PlanLimitsTab limits={limits} usage={usage} /> },
              { id: 'timeline', label: 'Timeline', content: <TimelineTab user={user} joinedDate={joinedDate} isVerified={isVerified} /> },
              { id: 'settings', label: 'Settings', content: <SettingsTab user={user} /> },
            ]}
            className={cx('cardBody')}
          />
        </div>
      </div>
    </>
  )
}
